import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { toast } from '../../lib/shadcn/sonner'
import { useGetBagDimensions, useUpdateBagDimensions } from '../../hooks/backend/bagsPage'
import type { BagSize } from '../../utils/bagStatus'
import { HelpText } from '../../components/HelpText'

interface SizeDimensions {
  size: BagSize
  length_in: number | null
  width_in: number | null
  height_in: number | null
}

type Field = 'length' | 'width' | 'height'
type Draft = Record<Field, string>

const FIELDS: { key: Field; label: string }[] = [
  { key: 'length', label: 'Length (in)' },
  { key: 'width', label: 'Width (in)' },
  { key: 'height', label: 'Height (in)' },
]

const s = (n: number | null) => (n === null ? '' : String(n))

export default function BagDimensionsSettings() {
  const getBagDimensions = useGetBagDimensions()
  const updateBagDimensions = useUpdateBagDimensions()
  const data = getBagDimensions.data as { sizes: SizeDimensions[] } | null | undefined

  const [drafts, setDrafts] = useState<Record<string, Draft>>({})
  const [busy, setBusy] = useState<BagSize | null>(null)

  useEffect(() => {
    void getBagDimensions.trigger({}, { skipCache: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!data) return
    const next: Record<string, Draft> = {}
    for (const d of data.sizes) {
      next[d.size] = { length: s(d.length_in), width: s(d.width_in), height: s(d.height_in) }
    }
    setDrafts(next)
  }, [data])

  const save = async (size: BagSize) => {
    const draft = drafts[size]
    if (!draft) return
    const values: Record<Field, number | null> = { length: null, width: null, height: null }
    for (const { key, label } of FIELDS) {
      const raw = draft[key].trim()
      if (raw === '') continue
      const n = Number(raw)
      if (!Number.isFinite(n) || n <= 0) {
        toast.error(`${label.replace(' (in)', '')} must be greater than 0`)
        return
      }
      values[key] = n
    }
    try {
      setBusy(size)
      const result = (await updateBagDimensions.trigger({
        size,
        length_in: values.length,
        width_in: values.width,
        height_in: values.height,
      })) as { stores?: { store: string; updated: number; failed: string[] }[] } | undefined
      await getBagDimensions.trigger({}, { skipCache: true })
      const stores = result?.stores ?? []
      if (!stores.length) {
        toast.success(`${size} bag dimensions saved`)
      } else {
        const summary = stores.map(s => `${s.store} ${s.updated}`).join(', ')
        const failed = stores.flatMap(s => s.failed.map(f => `${s.store} ${f}`))
        if (failed.length) {
          console.error('Bag dimensions not sent to some variations', failed)
          toast.warning(`${size} bag dimensions saved. Sent to ${summary} variations; ${failed.length} couldn't be updated.`)
        } else {
          toast.success(`${size} bag dimensions saved and sent to ${summary} variations`)
        }
      }
    } catch {
      toast.error('Failed to save bag dimensions')
    } finally {
      setBusy(null)
    }
  }

  const setDraft = (size: BagSize, patch: Partial<Draft>) =>
    setDrafts(d => ({
      ...d,
      [size]: { ...(d[size] ?? { length: '', width: '', height: '' }), ...patch },
    }))

  return (
    <Card id="bag-dimensions">
      <CardHeader>
        <CardTitle>Bag Dimensions</CardTitle>
        <CardDescription><HelpText>
          The size of each bag in inches. Saving here sends the size to every B2B and Web
          product of that size right away; saving a product sends it too, and Faire gets it
          when the product is first listed there.
          Dimensions are only sent once length, width and height are all filled in.
        </HelpText></CardDescription>
      </CardHeader>
      <CardContent>
        {getBagDimensions.error && !data ? (
          <p className="text-sm text-destructive">Couldn't load bag dimensions.</p>
        ) : !data ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : (
          <div className="divide-y rounded-md border">
            {data.sizes.map(d => {
              const draft = drafts[d.size] ?? { length: '', width: '', height: '' }
              return (
                <div
                  key={d.size}
                  className="grid grid-cols-1 gap-3 px-3 py-3 sm:grid-cols-[6rem,1fr,1fr,1fr,auto] sm:items-end"
                >
                  <span className="font-medium sm:pb-2">{d.size}</span>
                  {FIELDS.map(({ key, label }) => (
                    <div key={key} className="space-y-1.5">
                      <Label htmlFor={`bag-${key}-${d.size}`}>{label}</Label>
                      <Input
                        id={`bag-${key}-${d.size}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={draft[key]}
                        onChange={e => setDraft(d.size, { [key]: e.target.value })}
                        className="h-9"
                      />
                    </div>
                  ))}
                  <Button
                    type="button"
                    onClick={() => void save(d.size)}
                    disabled={busy === d.size}
                    className="gap-1.5 shrink-0"
                  >
                    {busy === d.size && <Loader2 className="h-4 w-4 animate-spin" />}
                    Save
                  </Button>
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
